import { createRef, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { map, isObject, isNil, isFunction, includes, isEmpty } from "lodash"
import classNames from "classnames"
import { TableRow, TableCell, Link } from "@mui/material"
import { KeyboardArrowLeft, Link as LinkIcon } from "@mui/icons-material"
import { formatLabel, treatAsFactor } from "helpers/securities"
import { TABLE_GROUP_DROPDOWN } from "helpers/shapes"
import { ITEM, GROUP_ITEM, COLUMN_GROUP } from "components/securities-table/row/shapes"
import TableDropdown from "components/table-dropdown"
import CellTooltip from "components/securities-table/cell-tooltip"
import PositionTooltip from "components/position-tooltip"
import { recursiveStickyCheck } from "components/securities-table/helpers"
import EditableField from "components/securities-table/editable-field"
import { FIELDS } from "helpers/simulator"

function getCellContents(getter, item, id, columnId) {
  const contents = getter(item, id, columnId)
  let value, tooltipContents
  if (isObject(contents)) {
    ({ value, tooltipContents } = contents)
  } else {
    value = contents
  }
  return { value, tooltipContents }
}

export default function Row(props) {
  const {
    item,
    columns: columnGroups,
    openSecurityProfile,
    openFactorProfile,
    rowIdClickOverride,
    groupDropdown,
    dispatch,
    isActive,
    isActiveTradingDisabled,
    onRowClick,
    onChange,
    editingField,
    editingRow,
    setEditingRow,
    setEditingField,
    setEditSecurity,
    className,
  } = props

  const inputRef = createRef()

  const handleRowClick = useCallback((event, row) => {
    if (!isFunction(onRowClick)) {
      return
    }
    onRowClick(event, row)
  }, [onRowClick])

  const onRowIdClick = useCallback(() => {
    const { id: securityId, assetClass, ticker, mic, isGroup } = item

    if (isGroup) {
      rowIdClickOverride(item)
    } else if (treatAsFactor(assetClass)) {
      openFactorProfile(securityId)
    } else {
      openSecurityProfile(assetClass, securityId, ticker, mic)
    }
  }, [item, openFactorProfile, openSecurityProfile, rowIdClickOverride])

  const getItem = useCallback(({ formatter, value, item, makeClick }) => {
    // This allows the selector passing the items to the table to control if the element is clickable or not
    const click = makeClick && makeClick(item)
    const displayValue = isFunction(formatter) ? formatter(value) : value
    if (click && !isNil(value)) {
      return <Link
        component="button"
        onClick={(e) => { e.preventDefault(); dispatch(click(item)) }}
        underline="hover"
      >
        {displayValue}
      </Link>
    }
    return displayValue
  }, [dispatch])

  const { id: securityId, label, isDrillDown, action, isGroup, isUnmodeled, isNoLongerHeld, isNotHeld, isAggregate, isEditable, pinned } = item
  const { columns: groupColumns, items: groupItems, selectedId: groupSelectedId, backAction } = groupDropdown ?? {}
  const isEditableRow = includes(["rebalanced", "delta"], item.id)

  const formattedIdentifier = useMemo(() =>
    (<div className="row-label">{label || formatLabel(item)}</div>),
  [item, label])

  const rowLabel = useMemo(() => {
    if (isDrillDown) {
      return <div className="id drill-down-link" href="#">
        <a className="back-button" onClick={() => dispatch(backAction)}><KeyboardArrowLeft /></a>
        <TableDropdown
          label={formattedIdentifier}
          columns={groupColumns}
          items={groupItems}
          selectedItemId={groupSelectedId}
          onItemClick={(group) => dispatch(action(group.id))}
          isActive={isActive}
        />
      </div>
    }

    return isEditableRow || isAggregate || isUnmodeled ? formattedIdentifier : <a className="id" href="#" onClick={onRowIdClick}>{formattedIdentifier}{!isGroup && <LinkIcon className="icon" />}</a>
  }, [isDrillDown, isEditableRow, isAggregate, isUnmodeled, formattedIdentifier, onRowIdClick, isGroup, groupColumns, groupItems, groupSelectedId, isActive, dispatch, backAction, action])

  const makeOnClick = useCallback((id) => () => {
    if (id === FIELDS.activePercentEquity && isActiveTradingDisabled) { return }
    setEditingRow(item.id)
    setEditingField(id)
  }, [isActiveTradingDisabled, setEditingRow, setEditingField, item])

  // Map applied to individual columns
  const columnMap = useCallback((columns, category, groupGetter) =>
    map(columns, ({ id, path, formatter, makeClick, isSticky, editable, getter: columnGetter }, index) => {
      const { value, tooltipContents } = getCellContents(columnGetter ?? groupGetter, item, path || id, id)
      const content = getItem({ formatter, value, item, makeClick })
      let clickableProps = {}
      if (includes(["economicExposure", "positionPercentEquity", "activePercentEquity"], id)) {
        clickableProps = {
          "data-id": id,
          onClick: makeOnClick(id),
          onMouseDown: (e) => e.preventDefault(),
        }
      }

      let tableCellContent = <span className={classNames({ "not-clickable": isEmpty(clickableProps) })} {...clickableProps}>
        {!tooltipContents ? content : <div className="cell-content-wrapper">
          <CellTooltip contents={tooltipContents} />
          {content}
        </div>}
      </span>

      if (editable && isEditable && id === editingField && item.id === editingRow) {
        tableCellContent = <EditableField
          ref={inputRef}
          id={id}
          item={item}
          content={content}
          onBlur={() => {
            setEditSecurity(null)
            setEditingRow("rebalanced")
            setEditingField(null)
          }}
          onChange={(...args) => onChange(...args, editingRow, editingField)}
          className={classNames("editable-cell", id)}
        />
      }

      return <TableCell
        key={`${securityId}${category}${id}`}
        size="small"
        align="right"
        className={classNames(id, { "group-start": index === 0, "sticky-left": isSticky, editable, disabled: id === FIELDS.activePercentEquity && isActiveTradingDisabled })}
        data-id={id}
      >
        {tableCellContent}
      </TableCell>
    }), [item, getItem, isEditable, editingField, editingRow, securityId, isActiveTradingDisabled, makeOnClick, inputRef, setEditSecurity, setEditingRow, setEditingField, onChange])

  // Map applied to groups of columns
  const groupColumnMap = useCallback((groupColumns) => map(groupColumns, ({ category, getter: groupGetter, columns }) => columnMap(columns, category, groupGetter)), [columnMap])

  const content = useMemo(() => {
    return <TableRow
      key={securityId}
      onClick={(e) => handleRowClick(e, item)}
      className={classNames({ "aggregation-row": isAggregate, "drill-down": isDrillDown, group: isGroup, "not-held": isNotHeld, "no-longer-held": isNoLongerHeld, "is-editing": isEditable, pinned }, className)}
    >
      <TableCell className={classNames("group-end identifier", { "sticky-left": recursiveStickyCheck(columnGroups), "display-name-unmodeled": isUnmodeled })} size="small">
        {rowLabel}
      </TableCell>

      {groupColumnMap(columnGroups)}
    </TableRow>
  }, [isAggregate, isDrillDown, isGroup, isNotHeld, isNoLongerHeld, isEditable, pinned, className, securityId, columnGroups, isUnmodeled, rowLabel, groupColumnMap, handleRowClick, item])

  return isEditableRow ? content : <PositionTooltip
    position={!isAggregate ? item : null}
    isActive={isActive}
    classes={{ popper: "security-description-container force", tooltip: "security-description" }}
    noWrapper
  >
    {content}
  </PositionTooltip>
}

Row.propTypes = {
  item: PropTypes.oneOfType([ITEM, GROUP_ITEM]),
  columns: PropTypes.arrayOf(COLUMN_GROUP),
  selected: PropTypes.bool,
  disableRowSelection: PropTypes.bool,
  isDrillDown: PropTypes.bool,
  isActive: PropTypes.bool,
  isActiveTradingDisabled: PropTypes.bool,
  groupDropdown: TABLE_GROUP_DROPDOWN,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  openSecurityProfile: PropTypes.func,
  openFactorProfile: PropTypes.func,
  rowIdClickOverride: PropTypes.func,
  dispatch: PropTypes.func,
  onRowClick: PropTypes.func,
  editingField: PropTypes.string,
  editingRow: PropTypes.string,
  setEditSecurity: PropTypes.func,
  setEditingField: PropTypes.func,
  setEditingRow: PropTypes.func,
  className: PropTypes.string,
}
